import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { CheckIcon, CopyIcon } from 'lucide-react';

interface CopyButtonProps {
    value: string;
    variant?: 'outline' | 'ghost' | 'default';
    size?: 'sm' | 'icon' | 'default';
    onCopySuccess?: () => void;
}

export const CopyButton = ({ value, variant = 'outline', size = 'icon', onCopySuccess }: CopyButtonProps) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast({
                title: t('openApiKeys.copyButton.copiedTitle'),
                description: t('openApiKeys.copyButton.copiedDescription'),
            });
            onCopySuccess?.();
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast({
                title: t('openApiKeys.copyButton.failedTitle'),
                description: t('openApiKeys.copyButton.failedDescription'),
                variant: 'destructive',
            });
            setCopied(false);
        }
    };

    return (
        <Button variant={variant} size="sm" className="h-7 px-2 shrink-0" onClick={handleCopy}>
            {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
        </Button>
    );
};
